"use client";
import { Dialog } from "@softmax/design-system";
import {
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";
import type { CredentialDialogState } from "./credential-state";
import { CredentialForm } from "./credential-form";
import { CredentialSecret } from "./credential-secret";

export function CredentialDialog({
  open,
  title,
  state,
  finalFocusRef,
  onClose,
  onNameChange,
  onWriteChange,
  onSubmit,
  onCopy,
  recovery,
  onRefresh,
  disabled = false,
  notice,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly state: CredentialDialogState;
  readonly finalFocusRef?: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
  readonly onNameChange: (name: string) => void;
  readonly onWriteChange: (allowed: boolean) => void;
  readonly onSubmit: () => void;
  readonly onCopy: () => void;
  readonly recovery?: {
    readonly refreshing: boolean;
    readonly message?: string;
  };
  readonly onRefresh?: () => void;
  readonly disabled?: boolean;
  readonly notice?: ReactNode;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const copyButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open && state.kind === "secret") copyButton.current?.focus();
  }, [open, state.kind]);
  const pending = state.kind !== "secret" && state.pending;
  return (
    <Dialog
      open={open}
      title={title}
      finalFocusRef={finalFocusRef}
      initialFocusRef={state.kind === "secret" ? copyButton : input}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      {notice}
      {state.kind === "secret" ? (
        <CredentialSecret
          state={state}
          disabled={disabled}
          copyButton={copyButton}
          onClose={onClose}
          onCopy={onCopy}
        />
      ) : (
        <CredentialForm
          state={state}
          disabled={disabled}
          id={id}
          input={input}
          onClose={onClose}
          onNameChange={onNameChange}
          onWriteChange={onWriteChange}
          onSubmit={onSubmit}
          recovery={recovery}
          onRefresh={onRefresh}
        />
      )}
    </Dialog>
  );
}
